/* Klubregister — reine Funktionen, keine Texte, kein DOM, kein Supabase.
 *
 * "Verein" meint hier einen Fußballverein, bei dem Spieler spielten oder spielen (Tabelle football_clubs,
 * supabase/klubregister-1-tabelle.sql). NICHT zu verwechseln mit domain/clubs.js: Das ist der Club-Wechsler
 * der App ("Grassrunners FC" als Mandant).
 *
 * Wichtig: clubKey() muss dieselbe Regel anwenden wie der eindeutige Index der Datenbank,
 *   lower(regexp_replace(name, '\s+', '', 'g'))
 * Groß/Klein und Leerzeichen zählen nicht, Akzente und Satzzeichen zählen (Entscheidung Bastian, N3).
 *
 * Übergangsregel (Konzept, Abschnitt 5): Bis die alten Textfelder stillgelegt werden, trägt jedes Objekt,
 * das einen Verein nennt, den Verweis UND Name und Logo-Pfad als Text (stampClub). Gelesen wird der Verweis
 * aus dem Register; fehlt er oder das Register, gilt der Text (resolveClub).
 */

export const CLUB_NAME_MAX = 80;

/** Ordner der Register-Logos im Speicher. Unter avatars/, damit die bestehenden Speicherregeln greifen. */
export const REGISTER_LOGO_ROOT = 'avatars/clubs';

function str(v) {
  return typeof v === 'string' ? v : v == null ? '' : String(v);
}

/** Name wie er gespeichert wird: Ränder weg, innere Leerzeichenfolgen zu einem Leerzeichen. */
export function cleanClubName(name) {
  return str(name).replace(/\s+/g, ' ').trim();
}

/** Schlüssel eines Namens — identisch zur Datenbankregel (kleingeschrieben, ohne jedes Leerzeichen). */
export function clubKey(name) {
  return str(name).replace(/\s+/g, '').toLowerCase();
}

/** Verein mit demselben Schlüssel (außer `exceptId`), sonst null. */
export function findDuplicate(clubs, name, exceptId = null) {
  const key = clubKey(name);
  if (!key) return null;
  return (clubs || []).find((c) => c.id !== exceptId && clubKey(c.name) === key) || null;
}

/**
 * Prüfung eines Namens vor dem Speichern. Liefert { ok, errors, duplicate }; errors sind Schlüssel unter
 * clubRegister.validation.* (nameRequired, nameTooLong, nameExists).
 */
export function validateClubName(name, clubs, exceptId = null) {
  const errors = [];
  const cleaned = cleanClubName(name);
  if (!cleaned) errors.push('nameRequired');
  if (cleaned.length > CLUB_NAME_MAX) errors.push('nameTooLong');
  const duplicate = cleaned ? findDuplicate(clubs, cleaned, exceptId) : null;
  if (duplicate) errors.push('nameExists');
  return { ok: errors.length === 0, errors, duplicate };
}

/** Zeile der Datenbank -> Objekt der App. */
export function rowToClub(row) {
  const r = row || {};
  return {
    id: r.id,
    name: str(r.name),
    logoPath: r.logo_path || null,
    createdAt: str(r.created_at),
    updatedAt: str(r.updated_at),
  };
}

/** Alphabetisch, ohne Beachtung von Groß/Klein und Akzenten. Verändert die Eingabe nicht. */
const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });
export function sortClubs(clubs) {
  return (clubs || []).slice().sort((a, b) => collator.compare(a.name, b.name));
}

/** Suche im Namen (ohne Beachtung von Groß/Klein und Leerzeichen); leere Suche = alle. */
export function filterClubs(clubs, query) {
  const q = clubKey(query);
  if (!q) return clubs || [];
  return (clubs || []).filter((c) => clubKey(c.name).includes(q));
}

/** Verein ohne Logo — in der Liste als "Logo fehlt" markiert (Entscheidung N6). */
export function lacksLogo(club) {
  return !(club && club.logoPath);
}

/** Karte id -> Verein. */
export function clubsById(clubs) {
  const map = new Map();
  for (const c of clubs || []) map.set(c.id, c);
  return map;
}

/**
 * Was zeigt die Oberfläche für einen Verein? Der Verweis aus dem Register gewinnt; fehlt der Verweis
 * (Altbestand) oder der Verein im geladenen Register (Laden fehlgeschlagen), gilt der Text.
 *
 * @param {Map|null} byId  clubsById(...) oder null, solange das Register nicht geladen ist
 * @returns {{ id: string|null, name: string, logoPath: string|null, fromRegister: boolean }}
 */
export function resolveClub(byId, id, fallbackName = '', fallbackLogoPath = null) {
  const club = id && byId ? byId.get(id) : null;
  if (club) {
    /* Hat der Verein im Register kein Logo, bleibt das alte Logo als Rückfall (wie die View players_public). */
    return { id: club.id, name: club.name, logoPath: club.logoPath || fallbackLogoPath || null, fromRegister: true };
  }
  return { id: id || null, name: str(fallbackName), logoPath: fallbackLogoPath || null, fromRegister: false };
}

/** Der aktuelle Verein eines Spielers (Player Info, Zeile auf AFH Transfers, Panel-Kopf, Suche, Export). */
export function playerClub(byId, player) {
  const p = player || {};
  return resolveClub(byId, p.currentClubId, p.currentClub, p.currentClubLogoPath);
}

/** Von- oder Zu-Seite eines Transfers. side: 'from' | 'to'. */
export function transferClub(byId, transfer, side) {
  const t = transfer || {};
  return resolveClub(byId, t[`${side}ClubId`], t[`${side}Club`], t[`${side}ClubLogoPath`]);
}

/**
 * Verein in ein Objekt eintragen (Spieler oder Transfer-Entwurf): Verweis UND Text (Übergangsregel).
 * prefix: 'currentClub' (Spieler) | 'fromClub' | 'toClub'. club null = Verein entfernen.
 * Verändert `target` und gibt es zurück.
 */
export function stampClub(target, prefix, club) {
  target[`${prefix}Id`] = club ? club.id : null;
  target[prefix] = club ? club.name : '';
  target[`${prefix}LogoPath`] = club ? club.logoPath || null : null;
  return target;
}

/** Wird ein Verein gerade benutzt? usage = { players, transfers } aus football_club_usage(). */
export function usageTotal(usage) {
  const u = usage || {};
  return (Number(u.players) || 0) + (Number(u.transfers) || 0);
}

/** Löschen nur ohne Benutzung. Fehlt die Zahl (nicht geladen), ist Löschen NICHT erlaubt. */
export function canDeleteClub(usage) {
  return !!usage && usageTotal(usage) === 0;
}

/** Zielordner einer Logo-Kopie im Register. */
export function registerLogoFolder(clubId) {
  return `${REGISTER_LOGO_ROOT}/${clubId}`;
}

/** Liegt das Logo schon im Registerordner dieses Vereins? */
export function logoIsInRegister(club) {
  return !!(club && club.logoPath && club.logoPath.startsWith(`${registerLogoFolder(club.id)}/`));
}

/** Zielpfad der Kopie: Ordner des Vereins + Dateiname der Quelle (bleibt so bei Wiederholung gleich). */
export function registerLogoTarget(clubId, sourcePath) {
  const base = str(sourcePath).split('/').pop() || 'logo';
  return `${registerLogoFolder(clubId)}/${base}`;
}

/* ------------------------------------------------------------------ Datenbankstand ohne die Spalten */

/**
 * Meldet die Datenbank, dass die Spalten für den Vereins-Verweis (klubregister-4-spalten.sql) fehlen?
 * PostgREST: PGRST204 (Spalte im Schema-Cache unbekannt) oder Postgres 42703 (undefined_column).
 */
export function isMissingClubColumn(error) {
  if (!error) return false;
  const code = String(error.code || '');
  const msg = String(error.message || '');
  if (code !== 'PGRST204' && code !== '42703' && !/column/i.test(msg)) return false;
  return /current_club_id|from_club_id|to_club_id/.test(msg);
}
